<script lang="ts">
  /**
   * Mandatory enrolment (the site administrator's second factor).
   *
   * The account that administers a site must carry a second factor, so the server confines its
   * session to enrolment: every other route answers 403 `enrollment_required`. This screen is
   * the only way forward, and it is deliberately not dismissible — the way out is to finish
   * enrolling, or to sign out.
   *
   * It is a separate component from the Settings panel because the situation is different: the
   * user has not chosen this, so it explains why, and it ends by showing the recovery codes
   * before letting them in.
   */
  import qrcode from 'qrcode-generator';
  import { ApiError, api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';

  let secret = $state('');
  let uri = $state('');
  let code = $state('');
  let recovery = $state<string[]>([]);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let copied = $state(false);
  let codeEl = $state<HTMLInputElement | null>(null);

  const qrSvg = $derived(uri ? qrSvgFor(uri) : '');

  $effect(() => {
    if (!uri && !recovery.length) void begin();
  });

  $effect(() => {
    if (uri) codeEl?.focus();
  });

  /** Inline SVG, so nothing is fetched and the CSP stays `default-src 'self'`. */
  function qrSvgFor(text: string): string {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let path = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) path += `M${c} ${r}h1v1h-1z`;
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" role="img" aria-label="QR">`
      + `<rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/>`
      + `<path d="${path}" fill="#000"/></svg>`;
  }

  async function begin(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaSetup();
      secret = r.secret;
      uri = r.uri;
    } catch (e) {
      // Already enrolled on another device in the meantime: just continue.
      if (e instanceof ApiError && e.code === 'already_enabled') return void store.enrollmentDone();
      error = t('adminFailed');
    } finally {
      busy = false;
    }
  }

  async function confirm(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaConfirm(code.trim());
      recovery = r.recovery;
      secret = '';
      uri = '';
      code = '';
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function copyRecovery(): Promise<void> {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      copied = true;
      setTimeout(() => { copied = false; }, 2000);
    } catch {
      /* clipboard blocked: the codes are on screen to copy by hand */
    }
  }
</script>

<div class="wrap">
  <div class="card">
    <img src="/img/logo.svg" alt="" width="48" height="48" />
    <h1>{t('enrollRequiredTitle')}</h1>

    {#if recovery.length}
      <p>{t('twoFactorRecoveryIntro')}</p>
      <ul class="recovery">
        {#each recovery as c (c)}<li>{c}</li>{/each}
      </ul>
      <div class="tik-actions">
        <button type="button" class="tik-btn" onclick={copyRecovery}>{copied ? t('twoFactorCopied') : t('twoFactorCopy')}</button>
        <button type="button" class="tik-btn primary" onclick={() => store.enrollmentDone()}>{t('enrollContinue')}</button>
      </div>
    {:else}
      <p class="why">{t('enrollRequiredWhy')}</p>
      <p class="tik-hint">{t('twoFactorScan')}</p>
      {#if qrSvg}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -- locally generated SVG, no user input -->
        <div class="qr">{@html qrSvg}</div>
        <p class="tik-hint">{t('twoFactorManual')}</p>
        <code class="secret" dir="ltr">{secret}</code>
      {/if}
      <label>
        <span>{t('twoFactorCode')}</span>
        <input
          type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" spellcheck="false"
          maxlength="8" bind:value={code} bind:this={codeEl} class="tik-input mono" />
      </label>
      {#if error}<p class="tik-error" role="alert">{error}</p>{/if}
      <div class="tik-actions">
        <button type="button" class="tik-btn primary" disabled={busy || code.trim().length < 6} onclick={confirm}>
          {t('twoFactorConfirm')}
        </button>
      </div>
    {/if}

    <button type="button" class="tik-btn ghost" onclick={() => void store.logout()}>{t('logout')}</button>
  </div>
</div>

<style>

  /* @css auth/EnrollRequired start */
  /* Controls come from the shared .tik-* classes in styles/global.css. */
  .wrap {
    min-block-size: 100%;
    display: grid;
    place-items: center;
    background: var(--surface-subtle);
    padding: var(--space-5);
  }
  .card {
    inline-size: min(420px, 100%);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--surface-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-card);
    padding: var(--space-6, 24px);
  }
  .card > img {
    align-self: center;
  }
  h1 {
    margin: 0;
    font-size: var(--text-h4, 18px);
    text-align: center;
    color: var(--text-strong);
  }
  p {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.7;
    color: var(--text-body);
  }
  /* Why this screen is unavoidable, stated where the person is stopped. */
  .why {
    background: var(--surface-subtle);
    border-radius: var(--radius-control);
    padding: var(--space-2) var(--space-3);
  }
  .qr {
    inline-size: 200px;
    align-self: center;
    background: #fff;
    padding: var(--space-2);
    border-radius: var(--radius-control);
  }
  .qr :global(svg) {
    display: block;
    inline-size: 100%;
    block-size: auto;
  }
  .secret {
    align-self: center;
    font-family: var(--font-mono);
    letter-spacing: 2px;
    background: var(--surface-sunken);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-control);
    user-select: all;
  }
  .recovery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .recovery li {
    font-family: var(--font-mono);
    background: var(--surface-sunken);
    border-radius: var(--radius-control);
    padding: var(--space-2);
    text-align: center;
    direction: ltr;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-strong);
  }
  .tik-actions {
    display: flex;
    gap: var(--space-2);
  }
  .tik-actions :global(.tik-btn) {
    flex: 1;
  }
  /* @css auth/EnrollRequired end */
</style>
